/**
 * How long the market has left, or how long until it opens.
 *
 * A grid bot's orders are day orders: they die at the close and are re-posted
 * at the next open. Knowing there are four minutes left is the difference
 * between letting a cycle finish and watching it expire, so the countdown sits
 * on every screen where a bot or a price is being watched.
 *
 * Friday's Jumma pause is shown as a break, not a close, because the orders
 * survive it — calling it a close would be a lie about what happens to them.
 *
 * Purely local arithmetic on a PKT wall clock: no request, no poll.
 */
import { useEffect, useState } from 'react';
import { getMarketCountdown, type MarketCountdown } from '../lib/marketHours';

const VERB: Record<MarketCountdown['kind'], string> = {
  opens: 'opens in',
  closes: 'closes in',
  breaks: 'breaks in',
  reopens: 'reopens in',
};

/**
 * Seconds while the hour is the thing you are watching, coarser beyond it —
 * nobody needs the seconds on a countdown to Monday.
 */
function format(ms: number): string {
  const total = Math.floor(ms / 1000);
  const d = Math.floor(total / 86_400);
  const h = Math.floor((total % 86_400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export const MarketClock = ({ compact = false }: { compact?: boolean }) => {
  const [countdown, setCountdown] = useState<MarketCountdown>(() => getMarketCountdown());

  // Inside the hour the figure changes every second; beyond it, it does not.
  const bySecond = countdown.msUntil < 3_600_000;
  useEffect(() => {
    const timer = setInterval(() => setCountdown(getMarketCountdown()), bySecond ? 1000 : 30_000);
    return () => clearInterval(timer);
  }, [bySecond]);

  const open = countdown.status === 'open';
  const closing = open && countdown.msUntil < 5 * 60_000;
  const tone = closing ? 'var(--tk-down)' : open ? 'var(--tk-up)' : 'var(--tk-ink-3)';
  const at = countdown.at.toLocaleTimeString('en-GB', { timeZone: 'Asia/Karachi', hour: '2-digit', minute: '2-digit', hour12: false });

  return (
    <span
      title={`Market ${countdown.status} · ${VERB[countdown.kind]} ${format(countdown.msUntil)} (${at} PKT)`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 9px',
        borderRadius: 999,
        background: open ? 'var(--tk-up-soft)' : 'var(--tk-surface-2)',
        border: '1px solid var(--tk-line)',
        fontSize: 11,
        fontFamily: 'var(--tk-mono)',
        fontVariantNumeric: 'tabular-nums',
        color: 'var(--tk-ink-2)',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{ width: 6, height: 6, borderRadius: 999, background: tone, flexShrink: 0, animation: open ? 'tk-pulse 2s ease-in-out infinite' : undefined }}
      />
      {!compact && <span style={{ color: 'var(--tk-ink-3)' }}>{VERB[countdown.kind]}</span>}
      <span style={{ fontWeight: 700, color: tone }}>{format(countdown.msUntil)}</span>
    </span>
  );
};
